<script setup lang="ts">
  import DateTimePicker from '@/components/form/DateTimePicker.vue'
  import ProductExtraOptions from '@/components/ProductExtraOptions.vue'
  import PlusIcon from '@/icons/plus-icon.vue'
  import { type EditJourneyForm } from '@/model/edit-journey.model.ts'
  import { useCarrierStore } from '@/store/carrier.store.ts'
  import { useDriversStore } from '@/store/driver.store'
  import { useJourneysStore } from '@/store/journey.store'
  import { useProductStore } from '@/store/product.store.ts'
  import { useTemplateStore } from '@/store/template.store.ts'
  import type { StatusOption } from '@/types/journey-template.ts'
  import type { Journey } from '@/types/journey.ts'
  import type { Product } from '@/types/product'
  import * as Sentry from '@sentry/browser'
  import {
    canEditJourneys,
    canEditProducts,
    generateDisabledFields,
    generateProductOptionalFields,
    getJourneyProducts,
    initializeEditJourneyForm,
    isExternalEdit,
    generateTouched as setTouched,
    validateEditJourney,
  } from '@/utils/bulk-edit'
  import { setTouched as setTouchedProducts } from '@/utils/product-utils'
  import { createEmptyJourneyProduct } from '@/utils/create-journey'
  import {
    DropDown,
    FormFieldContainer,
    FormInput,
    Modal,
    require,
    UiButton,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, ref, watch, type Ref } from 'vue'

  // Stores
  const productStore = useProductStore()
  const carrierStore = useCarrierStore()
  const journeysStore = useJourneysStore()
  const driversStore = useDriversStore()
  const templatesStore = useTemplateStore()

  const toastStore = useToastStore()

  const show: Ref<boolean> = ref(false)
  const externalJourneys = computed(() => isExternalEdit(journeys.value))
  const formData = ref<EditJourneyForm>(initializeEditJourneyForm([], driversStore.drivers))
  const journeys = ref<Journey[]>([])

  const { organizationProducts } = storeToRefs(productStore)

  const isSubmitting = ref(false)

  const availableDrivers = computed(() => {
    return driversStore.drivers.filter(driver => driver.vehicle != null)
  })

  const allowedTemplates = computed(() =>
    templatesStore.minimalTemplates.filter(template => template.intern != externalJourneys.value)
  )

  const projectProducts: Ref<Product[] | undefined> = ref()

  const originalProducts = ref<Product[]>([])
  const optionalProductFields = computed(() =>
    generateProductOptionalFields(formData.value, originalProducts.value)
  )

  const disabledFields = computed(() => {
    return generateDisabledFields(journeys.value, formData.value)
  })

  const errors = computed(() =>
    validateEditJourney(formData.value, journeys.value, originalProducts.value)
  )

  async function updateProjectProducts() {
    const projectUuid = journeys.value[0]?.stroomSignature.document.project.uuid
    if (projectUuid == null) return undefined

    const products = await productStore.fetchProjectProducts(projectUuid)

    const output = organizationProducts.value.filter(organizationProduct =>
      products?.some(projectProduct => projectProduct.uuid == organizationProduct.uuid)
    )

    return output
  }

  async function toggleModal() {
    show.value = !show.value

    if (show.value) {
      const { editable, errors } = canEditJourneys(journeys.value)

      if (!editable) {
        show.value = false
        toastStore.error(
          'Deze ritten kunnen niet worden aangepast: ' + errors.map(error => `\n- ${error}`),
          2000 + (errors.length - 1) * 1000
        )
        return
      }
      formData.value = initializeEditJourneyForm(journeys.value, driversStore.drivers)
      originalProducts.value = getJourneyProducts(journeys.value)
      touchedProducts.value = []

      for (let index = 0; index < originalProducts.value.length; index++) {
        touchedProducts.value[index] = setTouchedProducts(false)
      }

      isSubmitting.value = false

      projectProducts.value = await updateProjectProducts()

      touched.value = setTouched(false)
    } else {
      window.dispatchEvent(new Event('modal-closed'))
    }
  }
  const setSelectedData = (data: Journey[]) => {
    journeys.value = data
  }

  const statusOptions: StatusOption[] = [
    { id: 'AANGEKOMEN', label: 'Aangekomen' },
    { id: 'FINAAL', label: 'Finaal' },
  ]

  const touched = ref<Record<keyof EditJourneyForm, boolean>>(setTouched(false))
  const touchedProducts = ref<Record<keyof Product, boolean>[]>([])

  function fieldError(field: keyof EditJourneyForm) {
    if (!isSubmitting.value && !touched.value[field]) return undefined
    return errors.value.formErrors[field]
  }

  async function submitForm() {
    isSubmitting.value = true

    if (
      Object.values(errors.value.formErrors).some(error => error != null) ||
      Object.values(errors.value.productErrors).some(error => error != null)
    ) {
      touched.value = setTouched(true)

      for (let index = 0; index < touchedProducts.value.length; index++) {
        touchedProducts.value[index] = setTouchedProducts(true)
      }

      toastStore.error('Er zijn één of meerdere velden verkeerd ingevuld.')
    } else {
      try {
        await journeysStore.submitBulkEditJourneyForm(
          require(journeys.value, 'Journeys are not set'),
          formData.value
        )
      } catch (error) {
        toastStore.error('Er is iets fout gegaan. Bel klantenservice.', 10000)
        Sentry.captureException(error)

        isSubmitting.value = false
        return
      }
      toggleModal()
      window.reportDataTable.ajax.reload(null, false)
    }

    isSubmitting.value = false
  }

  watch(
    () => show.value,
    () => {
      if (!show.value) window.dispatchEvent(new Event('modal-closed'))
    }
  )

  watch(
    () => formData.value.template,
    (_, oldValue) => {
      // When a template is set in the form the products list in the form should be prefilled
      // if the products can be edited
      if (oldValue == null && canEditProducts(journeys.value, formData.value)) {
        formData.value.products = getJourneyProducts(journeys.value)
      }
    }
  )

  defineExpose({ toggleModal, setSelectedData })

  onMounted(async () => {
    await Promise.all([
      templatesStore.fetchAllTemplatesMinimal(),
      driversStore.fetchAllDriversFromOrganisation(),
      productStore.fetchOrganizationProducts(),
      carrierStore.fetchOrganizationCarriers(),
    ])
  })
</script>

<template>
  <Modal v-model:show="show">
    <template #header>
      <h3>Ritten aanpassen</h3>
    </template>

    <DropDown
      v-model="formData.template"
      :data="allowedTemplates"
      label="Stroom (Kenmerk)"
      :enable-search="true"
      :optional="true"
      idKey="stroomUUID"
      :error="fieldError('template')"
    >
      <template #empty
        >Selecteer een stroom om deze aan te passen voor alle geselecteerde ritten</template
      >
      <template #item="{ item }">
        <div class="selection-option flex-container">
          <span class="item-name">
            {{ item?.stroomName }}
          </span>
        </div>
      </template>
    </DropDown>

    <DropDown
      v-model="formData.carrierDriver"
      :data="availableDrivers"
      label="Kenteken"
      :disabled="disabledFields.disabledFields.includes('carrierDriver')"
      :enable-search="true"
      :optional="true"
      :error="fieldError('carrierDriver')"
    >
      <template #empty
        >Selecteer een kenteken om deze aan te passen voor alle geselecteerde ritten</template
      >
      <template #item="{ item }">
        <div class="selection-option flex-container">
          <span class="item-name">
            {{ item?.registration.licenseplate }}
          </span>
          <span class="item-name">
            {{ item?.vehicle?.organization.organisatieNaam }}
          </span>
        </div>
      </template>
    </DropDown>

    <FormInput
      v-model="formData.remark"
      label="Reden voor aanpassing"
      placeholder="Vul reden in"
      :error="fieldError('remark')"
      :disabled="disabledFields.disabledFields.includes('remark')"
      :maxLength="255"
      @blur="touched['remark'] = true"
    />

    <DateTimePicker
      v-model="formData.departureTimestamp"
      label="Vertrektijd"
      @blur="touched['departureTimestamp'] = true"
      :required="false"
      :error="fieldError('departureTimestamp')"
    />

    <DateTimePicker
      v-model="formData.arrivalTimestamp"
      label="Aankomsttijd"
      @blur="touched['arrivalTimestamp'] = true"
      :required="false"
      :error="fieldError('arrivalTimestamp')"
    />

    <DropDown
      v-model="formData.status"
      :disabled="disabledFields.disabledFields.includes('status')"
      :data="statusOptions"
      label="Status"
      :optional="true"
      :error="fieldError('status')"
      @blur="touched['status'] = true"
    >
      <template #empty
        >Selecteer een rit status om deze op alle geselecteerde ritten toe te passen</template
      >
      <template #item="{ item }">
        <div class="selection-option flex-container">
          <span class="item-name">{{ item.label }}</span>
        </div>
      </template>
    </DropDown>

    <FormFieldContainer label="Producten" :optional="true">
      <div v-if="formData.products != null" class="products-container">
        <ProductExtraOptions
          v-for="(_, index) of formData.products"
          :key="index"
          v-model="formData.products[index]!"
          v-model:touched="touchedProducts[index]!"
          :allProducts="projectProducts ?? organizationProducts"
          :searchableProducts="organizationProducts"
          :selectedProducts="formData.products"
          :placeholders="{ rdCode: 'rd code overschrijven', gewicht: 'gewicht overschrijven' }"
          :disabledFields="disabledFields.disabledProductFields[index] ?? []"
          :errors="errors.productErrors[index] ?? {}"
          :optionalFields="optionalProductFields[index] ?? []"
          :required="false"
          @remove="formData.products.splice(index, 1)"
        />
      </div>
      <div class="add-product">
        <button
          :disabled="disabledFields.disabledFields.includes('products')"
          @click="
            () => {
              if (formData.products == null) return

              formData.products.push(createEmptyJourneyProduct())
              touchedProducts.push(setTouchedProducts(false))
            }
          "
        >
          <span>
            <PlusIcon />
          </span>
        </button>
      </div>
    </FormFieldContainer>

    <FormFieldContainer
      label="Verklaring"
      :error="fieldError('userSignedTemplate')"
      class="signature-container"
    >
      <input
        type="checkbox"
        class="signature-checkbox"
        id="signature"
        v-model="formData.userSignedTemplate"
      />
      <label for="signature" class="signature-text">
        Ondergetekende verklaart dat het Identificatieformulier naar waarheid is ingevuld.
      </label>
    </FormFieldContainer>

    <template #footer="{ close }">
      <UiButton variant="default" type="submit" label="Annuleren" @click="close" />
      <UiButton
        :disabled="isSubmitting"
        variant="prominent"
        type="submit"
        label="Opslaan"
        @click="submitForm"
      />
    </template>
  </Modal>
</template>

<style scoped>
  label {
    position: relative;
    margin-top: 0.5rem;
  }

  .selection-option {
    display: flex;
    justify-content: space-between;
  }

  .required-field {
    color: red;
    position: absolute;
    top: 0;
    left: 100%;
  }

  .products-container {
    width: 100%;
  }

  .add-product {
    display: flex;
    gap: var(--spacing-xs);
    margin-top: var(--spacing-xs);
    margin-bottom: var(--spacing-s);
    align-self: end;

    button {
      margin-left: auto;
      width: var(--components-component-height);
      height: var(--components-component-height);
      border-radius: var(--components-form-field-corners);
      border: 1px solid var(--colours-grey-3);
      background: var(--colours-grey-1);
    }

    button:hover {
      background: var(--colours-support-hover);
    }
  }

  .signature-container {
    display: inline;
  }

  .signature-checkbox {
    cursor: pointer;
  }

  .signature-text {
    font-size: 1rem;
    margin: var(--spacing-s);
    margin-top: 0;
  }
</style>
